import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ExternalLink, FileText, Award, Mic } from 'lucide-react';
import SocialShare from './SocialShare';

const MediaSection = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isVisible, setIsVisible] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);

  const typeIcons = {
    'press': FileText,
    'news': Award,
    'blog': Mic
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Fetch media content from API
    const fetchMediaItems = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media`);
        const data = await response.json();
        if (data.success) {
          setMediaItems(data.data);
        }
      } catch (error) {
        console.error('Error fetching media items:', error);
        // Fallback to empty array if API fails
        setMediaItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMediaItems();
  }, []);

  const filters = [
    { key: 'all', label: 'All Media', count: mediaItems.length },
    { key: 'press', label: 'Press Releases', count: mediaItems.filter(item => item.media_type === 'press').length },
    { key: 'news', label: 'News & Articles', count: mediaItems.filter(item => item.media_type === 'news').length },
    { key: 'blog', label: 'Blog Posts', count: mediaItems.filter(item => item.media_type === 'blog').length }
  ];

  const filteredItems = selectedFilter === 'all' 
    ? mediaItems 
    : mediaItems.filter(item => item.media_type === selectedFilter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section id="media" ref={sectionRef} className="py-20 bg-gradient-to-br from-slate-50 to-blue-50/30 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-gradient-to-r from-indigo-200/20 to-blue-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="inline-flex items-center space-x-2 text-sm font-medium text-blue-600 bg-blue-100/80 px-4 py-2 rounded-full mb-4">
            <FileText size={16} />
            <span>Media Center</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Latest
            </span>
            <br />
            News & Updates
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Stay informed with our latest press releases, news coverage, and insights on government technology and digital transformation.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className={`flex flex-wrap justify-center gap-4 mb-12 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setSelectedFilter(filter.key)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
                selectedFilter === filter.key
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-blue-50 shadow-sm hover:shadow-md border border-gray-200'
              }`}
            >
              {filter.label}
              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                selectedFilter === filter.key
                  ? 'bg-white/20 text-white'
                  : 'bg-blue-100 text-blue-600'
              }`}>
                {filter.count}
              </span>
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading latest media content...</p>
          </div>
        )}

        {/* Media Grid */}
        {!loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => {
              const IconComponent = typeIcons[item.media_type] || FileText;
              
              return (
                <div
                  key={item.id || index}
                  className={`group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 cursor-pointer delay-${index * 100} ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                        <IconComponent size={48} className="text-blue-600 opacity-50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Type indicator */}
                    <div className="absolute top-4 left-4">
                      <div className="flex items-center space-x-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <IconComponent size={14} className="text-blue-600" />
                        <span className="text-xs font-medium text-gray-800 capitalize">{item.media_type}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Calendar size={14} />
                        <span>{formatDate(item.publication_date)}</span>
                      </div>
                      {item.read_time && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {item.read_time}
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 leading-tight">
                      {item.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                      {item.excerpt}
                    </p>

                    {item.category && (
                      <div className="mb-4">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {item.category}
                        </span>
                      </div>
                    )}

                    {/* Social Share Section */}
                    <div className="border-t border-gray-100 pt-4 mt-4">
                      <SocialShare
                        url={`${window.location.origin}/media/${item.id}`}
                        title={item.title}
                        description={item.excerpt}
                        showLabel={false}
                        size="sm"
                        variant="minimal"
                      />
                    </div>

                    {/* Read More Button */}
                    <div className="flex items-center justify-between mt-4">
                      <button className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-semibold group-hover:translate-x-1 transition-all duration-300">
                        <span>Read More</span>
                        <ExternalLink size={16} className="group-hover:rotate-12 transition-transform duration-300" />
                      </button>
                      
                      {item.author && (
                        <span className="text-xs text-gray-500">
                          By {item.author}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12">
            <FileText size={48} className="text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No media content found</h3>
            <p className="text-gray-600">
              {selectedFilter === 'all' 
                ? 'No media content has been published yet.' 
                : `No ${filters.find(f => f.key === selectedFilter)?.label.toLowerCase()} found.`
              }
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default MediaSection;